import { inject } from '@angular/core';
import { Routes } from '@angular/router';
import { environment } from 'src/environments/environment';
import { loginRedirectionGuardService } from 'src/services/login-redirection.guard.service';

import { CustomerModuleKey } from '../models/customer';
import { activeSessionGuard } from '../services/active-session.guard';
import { categoriesGuard } from '../services/categories.guard';
import { cognitoGuard } from '../services/cognito.guard';
import { courseAccessGuard } from '../services/course-access.guard';
import { courseDetailGuard } from '../services/course-detail.guard';
import { helpCategoryGuard } from '../services/help-category.guard';
import { masterclassAccessibleGuard } from '../services/masterclass-accessible.guard';
import { masterclassAvailableGuard } from '../services/masterclass-available.guard';
import { moduleGuard } from '../services/module.guard';
import { paymentEnrolmentsGuard } from '../services/payment-enrolments.guard';
import { AuthService } from '../shared-components/ng-login/auth/auth.service';
import { authGuard } from '../shared-components/ng-login/auth/auth-guard.service';

export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./main/main.component').then((mod) => mod.MainComponent),
    canActivate: [authGuard],
    runGuardsAndResolvers: 'always',
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: '/overview'
      },
      {
        path: 'overview',
        loadComponent: () => import('./overview/overview.component').then((mod) => mod.OverviewComponent)
      },
      {
        path: 'categories/:id',
        loadComponent: () =>
          import('./category/category-detail/category-detail.component').then((mod) => mod.CategoryDetailComponent),
        canActivate: [categoriesGuard]
      },
      {
        path: 'webinar',
        canActivate: [moduleGuard],
        data: {
          module: CustomerModuleKey.WEBINARS
        },
        loadComponent: () =>
          import('./webinar/webinar-main/webinar-main.component').then((mod) => mod.WebinarMainComponent),
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'search'
          },
          {
            path: 'search',
            loadComponent: () =>
              import('./webinar/webinar-main/webinar-search/webinar-search.component').then(
                (mod) => mod.WebinarSearchComponent
              )
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./webinar/webinar-detail/webinar-detail.component').then((mod) => mod.WebinarDetailComponent)
          }
        ]
      },
      {
        path: 'my-learning',
        loadComponent: () => import('./my-learning/my-learning.component').then((mod) => mod.MyLearningComponent),
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'courses/in-progress'
          },
          {
            path: 'courses',
            children: [
              {
                path: 'in-progress',
                loadComponent: () =>
                  import('./my-learning/courses-in-progress/courses-in-progress.component').then(
                    (mod) => mod.CoursesInProgressComponent
                  )
              },
              {
                path: 'saved',
                loadComponent: () =>
                  import('./my-learning/saved-courses/saved-courses.component').then(
                    (mod) => mod.SavedCoursesComponent
                  ),
                canActivate: [moduleGuard],
                data: {
                  module: CustomerModuleKey.HIDE_COURSE_CATALOG,
                  invert: true
                }
              },
              {
                path: 'completed',
                loadComponent: () =>
                  import('./my-learning/completed-courses/completed-courses.component').then(
                    (mod) => mod.CompletedCoursesComponent
                  )
              },
              {
                path: 'academic-services',
                canActivate: [paymentEnrolmentsGuard, moduleGuard],
                data: {
                  module: CustomerModuleKey.HIDE_COURSE_CATALOG,
                  invert: true
                },
                loadComponent: () =>
                  import('./my-learning/academic-services/academic-services.component').then(
                    (mod) => mod.AcademicServicesComponent
                  )
              }
            ]
          },
          {
            path: 'articles',
            children: [
              {
                path: 'saved',
                loadComponent: () =>
                  import('./my-learning/saved-articles/saved-articles.component').then(
                    (mod) => mod.SavedArticlesComponent
                  )
              }
            ]
          }
          // Not allowed route path until we have a report
          // https://nucleoo.atlassian.net/jira/software/c/projects/EDUNI/boards/138?selectedIssue=EDUNI-218

          // {
          //   path: 'my-follow-up',
          //   component: MyInsightsComponent
          // }
        ]
      },
      {
        path: 'elearning',
        loadComponent: () => import('./elearning/elearning.component').then((mod) => mod.ElearningComponent),
        canActivate: [moduleGuard],
        data: {
          module: CustomerModuleKey.HIDE_COURSE_CATALOG,
          invert: true
        },
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'home'
          },
          {
            path: 'home',
            loadComponent: () =>
              import('./elearning/elearning-overview/elearning-overview.component').then(
                (mod) => mod.ElearningOverviewComponent
              )
          },
          {
            path: 'masterclasses',
            loadComponent: () =>
              import('./masterclass/masterclass-overview/masterclass-overview.component').then(
                (mod) => mod.MasterclassOverviewComponent
              )
          },
          {
            path: 'webinars',
            loadComponent: () =>
              import('./webinar/webinar-main/webinar-search/webinar-search.component').then(
                (mod) => mod.WebinarSearchComponent
              )
          },
          {
            path: 'articles',
            loadComponent: () =>
              import('./articles/article-list/article-list.component').then((mod) => mod.ArticleListComponent)
          },
          {
            path: 'courses',
            loadComponent: () =>
              import('./course/course-search/course-search.component').then((mod) => mod.CourseSearchComponent)
          }
        ]
      },
      {
        path: 'masterclass',
        canActivate: [masterclassAvailableGuard],
        loadComponent: () =>
          import('./masterclass/masterclass-overview/masterclass-overview.component').then(
            (mod) => mod.MasterclassOverviewComponent
          )
      },
      {
        path: 'masterclass/:id',
        canActivate: [masterclassAvailableGuard, masterclassAccessibleGuard],
        loadComponent: () =>
          import('./masterclass/masterclass-detail/masterclass-detail.component').then(
            (mod) => mod.MasterclassDetailComponent
          )
      },
      {
        path: 'shared/masterclass/:id',
        canActivate: [masterclassAvailableGuard, masterclassAccessibleGuard],
        loadComponent: () =>
          import('./masterclass/masterclass-detail/masterclass-detail.component').then(
            (mod) => mod.MasterclassDetailComponent
          )
      },
      {
        path: 'profile',
        loadComponent: () => import('./account/account.component').then((mod) => mod.AccountComponent),
        children: [
          {
            path: '',
            loadComponent: () => import('./account/profile/profile.component').then((mod) => mod.ProfileComponent)
          }
        ]
      },
      {
        path: 'security',
        loadComponent: () => import('./account/account.component').then((mod) => mod.AccountComponent),
        children: [
          {
            path: '',
            loadComponent: () => import('./account/security/security.component').then((mod) => mod.SecurityComponent)
          }
        ]
      },
      {
        path: 'my-subscription',
        loadComponent: () => import('./account/account.component').then((mod) => mod.AccountComponent),
        canActivate: [moduleGuard],
        data: {
          module: CustomerModuleKey.HIDE_COURSE_CATALOG,
          invert: true
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./account/my-subscription/my-subscription.component').then((mod) => mod.MySubscriptionComponent)
          }
        ]
      },
      {
        path: 'payments',
        loadComponent: () => import('./account/account.component').then((mod) => mod.AccountComponent),
        canActivate: [moduleGuard],
        data: {
          module: CustomerModuleKey.HIDE_COURSE_CATALOG,
          invert: true
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./account/bill-and-payments/bill-and-payments.component').then(
                (mod) => mod.BillAndPaymentsComponent
              )
          }
        ]
      },
      {
        path: 'degrees',
        loadComponent: () => import('./account/account.component').then((mod) => mod.AccountComponent),
        canActivate: [moduleGuard],
        data: {
          module: CustomerModuleKey.HIDE_COURSE_CATALOG,
          invert: true
        },
        children: [
          {
            path: '',
            loadComponent: () => import('./account/degree/degree.component').then((mod) => mod.DegreeComponent)
          }
        ]
      },
      {
        path: 'my-documents',
        loadComponent: () => import('./account/account.component').then((mod) => mod.AccountComponent),
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./account/my-documents/my-documents.component').then((mod) => mod.MyDocumentsComponent)
          }
        ]
      },
      {
        path: 'account',
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: '/profile'
          },
          {
            path: 'profile',
            redirectTo: '/profile'
          },
          {
            path: 'security',
            redirectTo: '/security'
          },
          {
            path: 'my-subscription',
            redirectTo: '/my-subscription'
          },
          {
            path: 'payments',
            redirectTo: '/payments'
          },
          {
            path: 'degrees',
            redirectTo: '/degrees'
          },
          {
            path: 'my-documents',
            redirectTo: '/my-documents'
          }
        ]
      },
      {
        path: 'secretary/payments',
        redirectTo: '/payments',
        pathMatch: 'full'
      },
      {
        path: 'course/:id',
        loadComponent: () =>
          import('./course/course-detail/course-detail.component').then((mod) => mod.CourseDetailComponent),
        canActivate: [courseDetailGuard]
      },
      {
        path: 'marketplace-course/:id/detail',
        loadComponent: () =>
          import('./course/course-detail/course-detail.component').then((mod) => mod.CourseDetailComponent),
        canActivate: [courseDetailGuard]
      },
      {
        path: 'shared/course/:id',
        loadComponent: () =>
          import('./course/course-detail/course-detail.component').then((mod) => mod.CourseDetailComponent),
        canActivate: [courseDetailGuard]
      },
      {
        path: 'course/:id/moodle',
        loadComponent: () => import('./moodle-course/moodle-course.component').then((mod) => mod.MoodleCourseComponent),
        canActivate: [courseAccessGuard]
      },
      {
        path: 'courses',
        loadComponent: () =>
          import('./course/course-search/course-search.component').then((mod) => mod.CourseSearchComponent),
        canActivate: [moduleGuard],
        data: {
          module: CustomerModuleKey.HIDE_COURSE_CATALOG,
          invert: true
        }
      },
      {
        path: 'articles',
        loadComponent: () =>
          import('./articles/article-list/article-list.component').then((mod) => mod.ArticleListComponent)
      },
      {
        path: 'article/:id',
        loadComponent: () =>
          import('./articles/article-detail/article-detail.component').then((mod) => mod.ArticleDetailComponent)
      },
      {
        path: 'help-center',
        children: [
          {
            path: '',
            loadComponent: () => import('./help-center/help-center.component').then((mod) => mod.HelpCenterComponent)
          }
        ]
      },
      {
        path: 'no-permission-content',
        loadComponent: () =>
          import('./no-permission-to-access-content/no-permission-to-access-content.component').then(
            (mod) => mod.NoPermissionToAccessContentComponent
          )
      },
      {
        path: 'tutorship',
        loadComponent: () =>
          import('./help-center/tutorship/tutorship.component').then((mod) => mod.TutorshipComponent),
        canActivate: [helpCategoryGuard],
        data: {
          option: 'tutorship'
        }
      },
      {
        path: 'help-center/tutorship',
        redirectTo: 'tutorship',
        pathMatch: 'full'
      },
      {
        path: 'faqs',
        loadComponent: () => import('./faq/faq.component').then((mod) => mod.FaqComponent),
        canActivate: [moduleGuard],
        data: {
          module: CustomerModuleKey.HIDE_COURSE_CATALOG,
          invert: true
        }
      }
    ]
  },
  {
    path: 'login',
    loadComponent: () => import('./login/login.component').then((mod) => mod.LoginComponent),
    canActivate: [activeSessionGuard]
  },
  {
    path: 'course/:idSimo/checkout',
    loadComponent: () => import('./login/login.component').then((mod) => mod.LoginComponent),
    canActivate: [loginRedirectionGuardService]
  },
  {
    path: 'course/:idSimo/request',
    loadComponent: () => import('./login/login.component').then((mod) => mod.LoginComponent),
    canActivate: [loginRedirectionGuardService]
  },
  {
    path: 'subscription/checkout',
    loadComponent: () => import('./login/login.component').then((mod) => mod.LoginComponent),
    canActivate: [loginRedirectionGuardService]
  },
  {
    path: 'email-pass-login',
    loadComponent: () =>
      import('./login/email-pass-login/email-pass-login.component').then((mod) => mod.EmailPassLoginComponent),
    canMatch: [(): boolean => !environment.production]
  },
  {
    path: 'reset-password',
    canActivate: [cognitoGuard],
    loadComponent: () => import('./login/login.component').then((mod) => mod.LoginComponent)
  },
  {
    path: 'signup',
    canActivate: [cognitoGuard],
    loadComponent: () => import('./login/login.component').then((mod) => mod.LoginComponent)
  },
  {
    path: 'confirmUser',
    loadComponent: () => import('./confirm-user/confirm-user.component').then((mod) => mod.ConfirmUserComponent)
  },
  {
    path: 'hub',
    loadComponent: () => import('./hub/hub.component').then((mod) => mod.HubComponent),
    canMatch: [(): boolean => inject(AuthService).isValidHubDomain()]
  },
  {
    path: 'ui-guide',
    loadComponent: () => import('./design-guide/ui-guide.component').then((mod) => mod.UIGuideComponent)
  },
  // In order to avoid multiple calls to the backend keep wildcard as the last route in the route list
  {
    path: '**',
    redirectTo: ''
  }
];
